/**
 * <XtsModule> — Renders a single module: GLB mesh placed under a group whose
 * transform matches the chain's startWorldPose for that module.
 *
 * Shows a wireframe AABB placeholder while no GLB has loaded (or the GLB
 * 404s). Origin correction is applied from the calibration sidecar when
 * available.
 *
 * Selection: forwards click events with the ModuleRef and applies an
 * emissive tint to the GLB's materials (via useGltfHighlight) when
 * `selected`. Drive status: the same hook pulses the emissive intensity
 * at 1 Hz when warning / error is set, plus a small billboard
 * <DriveStatusIcon> floats above the module.
 */

import React, { Suspense, useContext, useMemo, useSyncExternalStore } from 'react';
import { Box3, Vector3 } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { GLTF } from 'three-stdlib';
import type { BuiltModule } from '../../geometry/ChainBuilder.js';
import type { Vec3 } from '../../types.js';
import { poseToMatrix4 } from '../../geometry/PathBuilder.js';
import { useGltfClone } from './useGltfClone.js';
import { useGltfHighlight } from './useGltfHighlight.js';
import { DriveStatusIcon } from './DriveStatusIcon.js';
import { ModuleCornerMarkers } from './ModuleCornerMarkers.js';
import {
  DEFAULT_ORIGIN_CORRECTION,
  type OriginCorrection,
} from '../../assets/SidecarLoader.js';
import { useModuleSidecar } from './useModuleSidecar.js';
import { useRailSidecar } from './useRailSidecar.js';
import { ModuleStatusStoreContext } from './ModuleStatusStore.js';
import type { RailType3D } from '../../types.js';

const DEG2RAD = Math.PI / 180;

interface XtsModuleProps {
  module: BuiltModule;
  glbUrl: string | null;
  originCorrection?: OriginCorrection;
  /** When set, overrides sidecar lookup and uses this URL as the sidecar. */
  assetsBaseUrl?: string;
  /** Skip sidecar lookup if true (consumers managing correction externally). */
  noSidecar?: boolean;
  showBoundingBox?: boolean;
  selected?: boolean;
  selectionColor?: string;
  highlightColor?: string;
  /**
   * Module's `ModuleEntry.globalNumber` (see types.ts). When the runtime
   * `ModuleStatusStore` carries an entry for this number, that entry
   * overrides the config-time `driveStatus`.
   */
  moduleGlobalNumber?: number;
  driveStatus?: { warning?: boolean; error?: boolean };
  showWarnings?: boolean;
  showErrors?: boolean;
  /** Show 8 red AABB-corner spheres for calibration alignment. */
  showCornerMarkers?: boolean;
  cornerMarkerSizeMm?: number;
  cornerMarkerColor?: string;
  /**
   * Beckhoff guiding-rail GLB URL + type to render alongside the module.
   * When set, an additional GLB scene is mounted under the same
   * module-local frame as the module, with its own origin-correction
   * sidecar applied. `null` skips the rail (e.g. for ATH modules whose
   * rail is baked into the module GLB, or for Hepco GFX rail systems).
   */
  guidingRail?: { railType: RailType3D; glbUrl: string } | null;
  /** Optional sidecar override for the rail's origin correction. */
  guidingRailCorrection?: OriginCorrection;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
}

const XtsModuleContent: React.FC<XtsModuleProps> = ({
  module,
  glbUrl,
  originCorrection,
  assetsBaseUrl = '/models',
  noSidecar = false,
  selected = false,
  selectionColor = '#FFE090',
  highlightColor,
  moduleGlobalNumber,
  driveStatus,
  showWarnings = true,
  showErrors = true,
  showCornerMarkers = false,
  cornerMarkerSizeMm = 10,
  cornerMarkerColor = '#FF2030',
  guidingRail,
  guidingRailCorrection,
  onClick,
}) => {
  // Subscribe to the imperative module-status store. When an entry
  // exists for this module's globalNumber it overrides the config-time
  // `driveStatus`. `useSyncExternalStore` keeps the subscription
  // narrowly scoped — only this module re-renders on status changes.
  const statusStore = useContext(ModuleStatusStoreContext);
  const liveStatus = useSyncExternalStore(
    useMemo(
      () => (listener: () => void) => {
        if (!statusStore || moduleGlobalNumber === undefined) return () => {};
        return statusStore.subscribe(moduleGlobalNumber, listener);
      },
      [statusStore, moduleGlobalNumber],
    ),
    () =>
      statusStore && moduleGlobalNumber !== undefined
        ? statusStore.get(moduleGlobalNumber)
        : undefined,
    () => undefined,
  );
  const effectiveDriveStatus = liveStatus ?? driveStatus;
  const matrix = useMemo(
    () => poseToMatrix4(module.startWorldPose),
    [module.startWorldPose],
  );

  const sidecar = useModuleSidecar(
    noSidecar ? ('AT2000_0250' as const) : module.moduleType,
    assetsBaseUrl,
  );
  const effectiveCorrection =
    originCorrection ??
    (noSidecar ? DEFAULT_ORIGIN_CORRECTION : sidecar.correction);

  // Hooks must run unconditionally (rules-of-hooks). Pass empty string when
  // no GLB to skip loading; the hook handles the empty case internally.
  const gltf = useGltfClone(glbUrl ?? '');
  const length = module.catalog.moduleLengthMm;
  // Selection tint (constant) + drive-status blink (pulse) on the
  // module's own GLB materials — clone-on-write so the shared cache
  // material stays untouched.
  useGltfHighlight(gltf, {
    selected,
    selectionColor,
    highlightColor,
    driveStatus: effectiveDriveStatus,
    showWarnings,
    showErrors,
  });

  // Pick which (if any) status icon to mount above the module.
  const iconKind: 'warning' | 'error' | null =
    effectiveDriveStatus?.error && showErrors !== false
      ? 'error'
      : effectiveDriveStatus?.warning && showWarnings !== false
        ? 'warning'
        : null;

  return (
    <group
      matrixAutoUpdate={false}
      matrix={matrix}
      onClick={onClick}
      // Pointer events bubble — child modules' clicks would hit the parent
      // group otherwise. Stop propagation to keep selection precise.
      onPointerDown={(e) => {
        if (onClick) e.stopPropagation();
      }}
    >
      {!glbUrl || !gltf ? (
        <PlaceholderMesh moduleLengthMm={length} />
      ) : (
        <ModuleGltfGroup
          gltf={gltf}
          originCorrection={effectiveCorrection}
          cornerMarkers={
            showCornerMarkers && sidecar.sidecar?.approximateBoundsMm
              ? {
                  bounds: sidecar.sidecar.approximateBoundsMm,
                  sizeMm: cornerMarkerSizeMm,
                  color: cornerMarkerColor,
                }
              : null
          }
        />
      )}
      {guidingRail && (
        <GuidingRailGltfGroup
          railType={guidingRail.railType}
          glbUrl={guidingRail.glbUrl}
          assetsBaseUrl={assetsBaseUrl}
          correctionOverride={guidingRailCorrection}
        />
      )}
      {iconKind && (
        <DriveStatusIcon
          kind={iconKind}
          positionMm={[length / 2, -48, 130]}
        />
      )}
    </group>
  );
};

/**
 * Module rows are stable for the lifetime of a configuration — only
 * sidecar overrides, selection and drive status flip during normal
 * use. React.memo with shallow-equal cuts the per-frame mover-position
 * cascade so static modules don't reconcile when only a mover moved.
 */
export const XtsModule: React.FC<XtsModuleProps> = React.memo((props) => (
  <Suspense
    fallback={
      <group matrixAutoUpdate={false} matrix={poseToMatrix4(props.module.startWorldPose)}>
        <PlaceholderMesh moduleLengthMm={props.module.catalog.moduleLengthMm} />
      </group>
    }
  >
    <XtsModuleContent {...props} />
  </Suspense>
));
XtsModule.displayName = 'XtsModule';

const PlaceholderMesh: React.FC<{ moduleLengthMm: number }> = ({
  moduleLengthMm,
}) => {
  return (
    <mesh position={[moduleLengthMm / 2, -48, 50]}>
      <boxGeometry args={[moduleLengthMm, 96, 100]} />
      <meshBasicMaterial color="#FFB000" wireframe />
    </mesh>
  );
};

const ModuleGltfGroup: React.FC<{
  gltf: GLTF;
  originCorrection: OriginCorrection;
  cornerMarkers: {
    bounds: { min: Vec3; max: Vec3 };
    sizeMm: number;
    color: string;
  } | null;
}> = ({ gltf, originCorrection, cornerMarkers }) => {
  return (
    <group
      position={originCorrection.translateMm}
      rotation={[
        originCorrection.rotationDegEuler[0] * DEG2RAD,
        originCorrection.rotationDegEuler[1] * DEG2RAD,
        originCorrection.rotationDegEuler[2] * DEG2RAD,
        'XYZ',
      ]}
    >
      <primitive object={gltf.scene} />
      {cornerMarkers && (
        <ModuleCornerMarkers
          bounds={cornerMarkers.bounds}
          sizeMm={cornerMarkers.sizeMm}
          color={cornerMarkers.color}
        />
      )}
    </group>
  );
};

/**
 * Renders a Beckhoff guiding-rail GLB in the module-local frame, with its
 * own origin-correction sidecar applied. The rail sits next to the motor
 * module so the mover wheels visually engage it.
 */
const GuidingRailGltfGroup: React.FC<{
  railType: RailType3D;
  glbUrl: string;
  assetsBaseUrl: string;
  correctionOverride?: OriginCorrection;
}> = ({ railType, glbUrl, assetsBaseUrl, correctionOverride }) => {
  const sidecar = useRailSidecar(railType, assetsBaseUrl);
  const gltf = useGltfClone(glbUrl);
  const correction = correctionOverride ?? sidecar.correction;
  if (!gltf) return null;
  return (
    <group
      position={correction.translateMm}
      rotation={[
        correction.rotationDegEuler[0] * DEG2RAD,
        correction.rotationDegEuler[1] * DEG2RAD,
        correction.rotationDegEuler[2] * DEG2RAD,
        'XYZ',
      ]}
    >
      <primitive object={gltf.scene} />
    </group>
  );
};

/** Compute approximate AABB of a built module for zoomToFit aggregation. */
export function approximateModuleBounds(module: BuiltModule): Box3 {
  const length = module.catalog.moduleLengthMm;
  const min = new Vector3(0, -50, 0);
  const max = new Vector3(length, 50, 100);
  const m = poseToMatrix4(module.startWorldPose);
  const box = new Box3(min, max);
  box.applyMatrix4(m);
  return box;
}
